import React, { useEffect, useState } from "react";
import { Plus, Trash2, MapPin } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Areas({ embedded = false }) {
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/areas");
    setAreas(data.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/areas", form);
      toast.success("Area created");
      setForm({ name: "", code: "", description: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this area?")) return;
    try {
      await api.delete(`/areas/${id}`);
      toast.success("Area deleted"); load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="areas-page">
      <div className="flex items-start justify-between">
        <div>
          {!embedded && (
            <>
              <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Organization</div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">Areas</h1>
            </>
          )}
          <p className="text-sm text-slate-500 mt-1">Operational zones grouping meters, households and depots.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="area-add-button" className="bg-blue-600 hover:bg-blue-700 rounded-sm">
              <Plus size={16} className="mr-1" /> New area
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="area-form-dialog">
            <DialogHeader><DialogTitle className="font-heading">Create area</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="area-name-input" className="mt-1" />
              </div>
              <div>
                <Label>Code (optional)</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
                  data-testid="area-code-input" className="mt-1" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="area-desc-input" className="mt-1" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="area-form-submit" className="bg-blue-600 hover:bg-blue-700">
                  {saving ? "Saving…" : "Create area"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="areas-grid">
        {areas.length === 0 && (
          <div className="col-span-full p-10 text-center text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm">
            No areas yet — create the first one.
          </div>
        )}
        {areas.map((a) => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`area-card-${a.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-sm bg-blue-50 text-blue-600 flex items-center justify-center">
                  <MapPin size={16} />
                </div>
                <div>
                  <div className="font-heading font-semibold text-slate-900">{a.name}</div>
                  {a.code && <div className="text-xs text-slate-500 font-mono">{a.code}</div>}
                </div>
              </div>
              <button onClick={() => remove(a.id)} data-testid={`area-delete-${a.id}`}
                className="text-slate-400 hover:text-red-600 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
            {a.description && <div className="text-sm text-slate-600 mt-3 leading-relaxed">{a.description}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
